import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import { type BingoTile } from '@/common/types/bingo';
import {
  BINGO_LINES,
  checkCompletedLines,
  clearNewlyCompletedLines,
  finishRolling,
  revealAllFromQueue,
  revealNextTile,
} from '@/redux/reducers/bingoReducer';
import store, { useAppDispatch, useAppSelector } from '@/redux/store';

import { BingoTile as BingoTileComponent } from '../BingoTile/BingoTile';

import css from './styles.module.scss';

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// Bounce duration of BingoTile (must match framer-motion transition)
const BOUNCE_DUR = 500;

const COLS = 6;
const TILE_SIZE = 56;
const GAP = 4;
const STEP = TILE_SIZE + GAP;
const PLAY_SIZE = 5 * TILE_SIZE + 4 * GAP; // 296
const GRID_W = COLS * TILE_SIZE + (COLS - 1) * GAP; // 356

const REVEAL_DELAY = 150;
const LINE_ANIM_PAUSE = 2500;

// ── Tooltip content ─────────────────────────────────────────────────────────

// Maps rewardType (camelCase / snake_case) → existing i18n title key
const REWARD_TITLE_KEYS: Record<string, string> = {
  // Currencies
  tgem: 'Tgem',
  dice: 'Dice',
  cash: 'Cash',
  starPoints: 'Star Points',
  boardStarPoints: 'Star Points',
  bingoBalls: 'Bingo Balls',
  // Tickets
  goldTicket: 'Gold Ticket',
  silverTicket: 'Silver Ticket',
  diamondTicket: 'Diamond Ticket',
  // Resources
  wood: 'Wood',
  cotton: 'Cotton',
  bulk: 'Bulk',
  iron: 'Iron',
  oil: 'Oil',
  precious: 'Precious',
  // Materials
  lumber: 'Lumber',
  textiles: 'Textiles',
  bricks: 'Bricks',
  paper: 'Paper',
  steel: 'Steel',
  plastic: 'Plastic',
  glass: 'Glass',
  fuels_and_oils: 'Fuels and Oils',
  microchips: 'Microchips',
  // Goods: Furniture
  furniture: 'Furniture',
  upholstered_furniture: 'Upholstered furniture',
  garden_furniture: 'Garden furniture',
  office_furniture: 'Office furniture',
  // Goods: Garment
  clothing: 'Clothing',
  footwear: 'Footwear',
  accessories: 'Accessories',
  sports_equipment: 'Sports equipment',
  // Goods: Printing
  books: 'Books',
  packaging: 'Packaging',
  photo_frames: 'Photo frames',
  stationery: 'Stationery',
  // Goods: Homeware
  home_textiles: 'Home textiles',
  lamps: 'Lamps',
  tableware: 'Tableware',
  electric_fireplaces: 'Electric fireplaces',
  // Goods: Tools
  hand_tools: 'Hand tools',
  garden_tools: 'Garden tools',
  electronic_tools: 'Electronic tools',
  machines: 'Machines',
  // Goods: Children
  soft_toys: 'Soft Toys',
  board_games: 'Board games',
  construction_toys: 'Construction toys',
  electronic_toys: 'Electronic toys',
  // Goods: Music
  guitar: 'Guitar',
  electric_guitar: 'Electric guitar',
  light_music: 'Light music',
  speakers: 'Speakers',
  // Goods: Electronics
  washing_machine: 'Washing machine',
  laptop: 'Laptop',
  vr_equipment: 'VR equipment',
  robotics: 'Robotics',
  // Goods: Transport
  car: 'Car',
  train: 'Train',
  airplane: 'Airplane',
  ship: 'Ship',
};

// Descriptions per rewardType
const REWARD_DESC_KEYS: Record<string, string> = {
  tgem: 'Premium currency for contracts and boosts',
  dice: 'Used for rolls in Roll Craft',
  cash: 'Main currency for building and upgrades',
  starPoints: 'Points for league progress and rewards',
  boardStarPoints: 'Points for league progress and rewards',
  goldTicket: 'Rare ticket with increased jackpot odds',
  silverTicket: 'Lottery ticket for prize draws',
  diamondTicket: 'Most valuable ticket with max jackpot odds',
  wood: 'Raw resource for production',
  cotton: 'Raw resource for production',
  bulk: 'Raw resource for production',
  iron: 'Raw resource for production',
  oil: 'Raw resource for production',
  precious: 'Raw resource for production',
  lumber: 'Processed material for goods production',
  textiles: 'Processed material for goods production',
  bricks: 'Processed material for goods production',
  paper: 'Processed material for goods production',
  steel: 'Processed material for goods production',
  plastic: 'Processed material for goods production',
  glass: 'Processed material for goods production',
  fuels_and_oils: 'Processed material for goods production',
  microchips: 'Processed material for goods production',
};

const KIND_TOOLTIPS: Partial<
  Record<BingoTile['kind'], { title: string; desc: string }>
> = {
  row_reward: {
    title: 'Row reward',
    desc: 'Complete all 5 cells in a row to claim',
  },
  column_reward: {
    title: 'Column reward',
    desc: 'Complete all 5 cells in a column to claim',
  },
  diagonal_reward: {
    title: 'Diagonal reward',
    desc: 'Complete the main diagonal to claim',
  },
};

function getTooltip(
  tile: BingoTile,
  t: (key: string) => string,
): { title: string; desc: string } {
  const kindTooltip = KIND_TOOLTIPS[tile.kind];

  if (kindTooltip) {
    return { title: t(kindTooltip.title), desc: t(kindTooltip.desc) };
  }

  const titleKey = REWARD_TITLE_KEYS[tile.rewardType];
  const descKey = REWARD_DESC_KEYS[tile.rewardType];

  return {
    title: titleKey ? t(titleKey) : tile.rewardType.replace(/_/g, ' '),
    desc: descKey ? t(descKey) : t('Finished product for contract fulfillment'),
  };
}

// ── Types ────────────────────────────────────────────────────────────────────

interface PlaneAnim {
  id: number;
  lineIdx: number;
}

// ── Grid ─────────────────────────────────────────────────────────────────────

// Precomputed scatter directions for 36 tile positions
const SCATTER_DIRS = Array.from({ length: 36 }, (_, i) => {
  const row = Math.floor(i / COLS);
  const col = i % COLS;
  // Direction away from center
  const cx = (col - 2.5) * 1.2;
  const cy = (row - 2.5) * 1.2;
  const mag = Math.max(Math.hypot(cx, cy), 0.5);

  return {
    x: (cx / mag) * (120 + ((i * 37) % 80)),
    y: (cy / mag) * (120 + ((i * 53) % 80)),
    rotate: ((i * 47) % 360) - 180,
  };
});

const BingoGrid: FC = () => {
  const dispatch = useAppDispatch();
  const tiles = useAppSelector((state) => state.bingo.tiles);
  const completedLines = useAppSelector((state) => state.bingo.completedLines);
  const isRolling = useAppSelector((state) => state.bingo.isRolling);
  const lastRevealedTile = useAppSelector(
    (state) => state.bingo.lastRevealedTile,
  );
  const skipAnimation = useAppSelector((state) => state.bingo.skipAnimation);
  const eventId = useAppSelector((state) => state.bingo.eventId);
  const cardIndex = useAppSelector((state) => state.bingo.cardIndex);

  const [flashingTiles, setFlashingTiles] = useState<Set<number>>(new Set());
  const [planes, setPlanes] = useState<PlaneAnim[]>([]);
  const [activeTileId, setActiveTileId] = useState<number | null>(null);
  const [scale, setScale] = useState(1);
  const [revealCounter, setRevealCounter] = useState(0);
  const [boardReady, setBoardReady] = useState(false);
  const [boardKey, setBoardKey] = useState(cardIndex);
  const [boardTransition, setBoardTransition] = useState<
    'stable' | 'scattering' | 'assembling'
  >('assembling');
  const animatingRef = useRef(false);
  const prevCardIndexRef = useRef<number | null>(null);

  // First load: wait for data, then assemble. Card change: scatter → assemble.
  useEffect(() => {
    if (!eventId) return; // No data yet

    // First load
    if (prevCardIndexRef.current === null) {
      prevCardIndexRef.current = cardIndex;
      setBoardKey(cardIndex);
      setBoardReady(true);
      setBoardTransition('assembling');
      const t = setTimeout(() => setBoardTransition('stable'), 600);

      return (): void => clearTimeout(t);
    }

    // Card changed
    if (cardIndex !== prevCardIndexRef.current) {
      prevCardIndexRef.current = cardIndex;
      setBoardTransition('scattering');
      const t1 = setTimeout(() => {
        setBoardKey(cardIndex);
        setBoardTransition('assembling');
      }, 500);
      const t2 = setTimeout(() => {
        setBoardTransition('stable');
      }, 1100);

      return (): void => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [eventId, cardIndex]);

  // Increment counter on each reveal to force re-animation on already-open tiles
  useEffect(() => {
    if (lastRevealedTile !== null) {
      setRevealCounter((c) => c + 1);
    }
  }, [lastRevealedTile]);

  const wrapRef = useRef<HTMLDivElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);

  const updateScale = useCallback((): void => {
    if (outerRef.current) {
      const w = outerRef.current.clientWidth;
      const h = outerRef.current.clientHeight;

      if (w > 0 && h > 0) setScale(Math.min(w, h) / GRID_W);
    }
  }, []);

  useEffect(() => {
    updateScale();
    const ro = new ResizeObserver(updateScale);

    if (outerRef.current) ro.observe(outerRef.current);

    return (): void => ro.disconnect();
  }, [updateScale]);

  useEffect(() => {
    const handler = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setActiveTileId(null);
      }
    };
    document.addEventListener('mousedown', handler);

    return (): void => document.removeEventListener('mousedown', handler);
  }, []);

  const handleTileClick = (id: number): void => {
    setActiveTileId((prev) => (prev === id ? null : id));
  };

  /**
   * Imperative animation chain. Reads fresh state from store.getState()
   * so there are no stale closures. Each step dispatches + waits.
   */
  const runRevealChain = useCallback(async (): Promise<void> => {
    if (animatingRef.current) return;
    animatingRef.current = true;

    try {
      if (skipAnimation) {
        dispatch(revealAllFromQueue());
      } else {
        // 1. Reveal tiles one by one
        let queueLen = store.getState().bingo.revealQueue.length;
        while (queueLen > 0) {
          dispatch(revealNextTile());
          queueLen = store.getState().bingo.revealQueue.length;
          await delay(REVEAL_DELAY + BOUNCE_DUR);
        }
      }

      // 2. Check for completed lines
      dispatch(checkCompletedLines());

      // 3. If lines completed → run flash animation
      const completed = store.getState().bingo.newlyCompletedLines;

      if (completed.length > 0) {
        const tilesToFlash = new Set<number>();
        const newPlanes: PlaneAnim[] = [];

        completed.forEach((lineIdx: number) => {
          BINGO_LINES[lineIdx].forEach((id) => tilesToFlash.add(id));

          if (lineIdx < BINGO_LINES.length - 1) {
            newPlanes.push({ id: Date.now() + lineIdx, lineIdx });
          }
        });

        setFlashingTiles(tilesToFlash);
        setPlanes((prev) => [...prev, ...newPlanes]);

        await delay(LINE_ANIM_PAUSE);

        setFlashingTiles(new Set());
        setPlanes((prev) =>
          prev.filter((p) => !newPlanes.find((n) => n.id === p.id)),
        );
        dispatch(clearNewlyCompletedLines());
      }
    } finally {
      // 4. Everything done — unlock button and allow modal to show
      dispatch(finishRolling());
      animatingRef.current = false;
    }
  }, [dispatch, skipAnimation]);

  // Trigger chain when isRolling becomes true
  useEffect(() => {
    if (isRolling && !animatingRef.current) {
      runRevealChain();
    }
  }, [isRolling, runRevealChain]);

  // On unmount: if animation is in progress, instantly apply all remaining results
  useEffect(() => {
    return (): void => {
      const s = store.getState().bingo;

      if (s.isRolling || s.revealQueue.length > 0) {
        dispatch(revealAllFromQueue());
        dispatch(checkCompletedLines());
        dispatch(clearNewlyCompletedLines());
        dispatch(finishRolling());
      }

      animatingRef.current = false;
    };
  }, [dispatch]);

  const activeTile =
    activeTileId !== null ? tiles.find((t) => t.id === activeTileId) : null;

  const gridNaturalSize = GRID_W;

  if (!boardReady) return <div ref={outerRef} className={css.outer} />;

  return (
    <div ref={outerRef} className={css.outer}>
      <div
        className={css.gridWrap}
        ref={wrapRef}
        style={{
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
          width: gridNaturalSize,
          height: gridNaturalSize,
        }}
      >
        <div className={css.grid} key={boardKey}>
          {tiles.map((tile, idx) => {
            const scatter = SCATTER_DIRS[idx] ?? SCATTER_DIRS[0];
            const stagger = idx * 0.02;

            return (
              <motion.div
                key={tile.id}
                style={{ width: TILE_SIZE, height: TILE_SIZE }}
                initial={
                  boardTransition === 'assembling'
                    ? {
                        x: scatter.x,
                        y: scatter.y,
                        rotate: scatter.rotate,
                        scale: 0,
                        opacity: 0,
                      }
                    : false
                }
                animate={
                  boardTransition === 'scattering'
                    ? {
                        x: scatter.x,
                        y: scatter.y,
                        rotate: scatter.rotate,
                        scale: 0,
                        opacity: 0,
                      }
                    : {
                        x: 0,
                        y: 0,
                        rotate: 0,
                        scale: 1,
                        opacity: 1,
                      }
                }
                transition={{
                  duration: boardTransition === 'stable' ? 0 : 0.45,
                  delay: boardTransition === 'stable' ? 0 : stagger,
                  ease:
                    boardTransition === 'scattering'
                      ? 'easeIn'
                      : [0.34, 1.56, 0.64, 1],
                }}
              >
                <BingoTileComponent
                  tile={tile}
                  isNewlyCompleted={tile.id === lastRevealedTile}
                  revealCounter={
                    tile.id === lastRevealedTile ? revealCounter : 0
                  }
                  isFlashing={flashingTiles.has(tile.id)}
                  onTileClick={handleTileClick}
                />
              </motion.div>
            );
          })}
        </div>

        {completedLines
          .filter((lineIdx) => lineIdx < BINGO_LINES.length - 1)
          .map((lineIdx) => (
            <BingoLineHighlight key={lineIdx} lineIdx={lineIdx} />
          ))}

        <AnimatePresence>
          {planes.map((p) => (
            <BingoPlaneFly key={p.id} lineIdx={p.lineIdx} />
          ))}
        </AnimatePresence>

        <AnimatePresence>
          {activeTile && (
            <BingoTooltip
              tile={activeTile}
              onClose={() => setActiveTileId(null)}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// ── Tooltip ──────────────────────────────────────────────────────────────────

const TOOLTIP_W = 160;

const BingoTooltip: FC<{ tile: BingoTile; onClose: () => void }> = ({
  tile,
}) => {
  const { t } = useTranslation();
  const row = Math.floor(tile.id / COLS);
  const col = tile.id % COLS;

  const tileCenter = col * STEP + TILE_SIZE / 2;
  const left = Math.max(
    0,
    Math.min(tileCenter - TOOLTIP_W / 2, GRID_W - TOOLTIP_W),
  );
  const arrowLeft = tileCenter - left;
  const showBelow = row < 3;
  const top = showBelow ? (row + 1) * STEP + 6 : row * STEP - 6;

  const { title, desc } = getTooltip(tile, t);

  return (
    <motion.div
      className={css.tooltip}
      style={{
        left,
        width: TOOLTIP_W,
        ...(showBelow ? { top } : { bottom: GRID_W - top }),
      }}
      initial={{ opacity: 0, y: showBelow ? -6 : 6, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: showBelow ? -4 : 4, scale: 0.95 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
    >
      <div
        className={css.tooltipArrow}
        data-below={showBelow}
        style={{ left: arrowLeft }}
      />
      <div className={css.tooltipInner}>
        <span className={css.tooltipTitle}>{title}</span>
        {desc && <span className={css.tooltipDesc}>{desc}</span>}
      </div>
    </motion.div>
  );
};

// ── SVG line highlight ────────────────────────────────────────────────────────

const BingoLineHighlight: FC<{ lineIdx: number }> = ({ lineIdx }) => {
  const line = BINGO_LINES[lineIdx];

  if (!line) return null;

  const first = line[0];
  const last = line[line.length - 1];
  const x1 = (first % COLS) * STEP + TILE_SIZE / 2;
  const y1 = Math.floor(first / COLS) * STEP + TILE_SIZE / 2;
  const x2 = (last % COLS) * STEP + TILE_SIZE / 2;
  const y2 = Math.floor(last / COLS) * STEP + TILE_SIZE / 2;

  return (
    <svg
      className={css.lineSvg}
      width={PLAY_SIZE}
      height={PLAY_SIZE}
      viewBox={`0 0 ${PLAY_SIZE} ${PLAY_SIZE}`}
      overflow="visible"
    >
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="rgba(130,224,36,0.7)"
        strokeWidth="3"
        strokeLinecap="round"
        filter="url(#bingoGlow)"
      />
      <defs>
        <filter id="bingoGlow">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
    </svg>
  );
};

// ── Plane ─────────────────────────────────────────────────────────────────────

const BingoPlaneFly: FC<{ lineIdx: number }> = ({ lineIdx }) => {
  const isRow = lineIdx < 5;
  const idx = isRow ? lineIdx : lineIdx - 5;
  const center = idx * STEP + TILE_SIZE / 2;

  if (isRow) {
    return (
      <motion.img
        src="/img/contract/plane.webp"
        alt=""
        className={css.plane}
        style={{ top: center - 18 }}
        initial={{ x: -80, opacity: 1 }}
        animate={{ x: PLAY_SIZE + 80, opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 1.1, ease: 'easeIn' }}
      />
    );
  }

  return (
    <motion.img
      src="/img/contract/plane.webp"
      alt=""
      className={css.plane}
      style={{ left: center - 18, rotate: 90 }}
      initial={{ y: -80, opacity: 1 }}
      animate={{ y: PLAY_SIZE + 80, opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.1, ease: 'easeIn' }}
    />
  );
};

export { BingoGrid };
